import { Mail, Phone, User } from 'lucide-react';
import {
  COMMUNICATION_MODE_LABELS,
  CommunicationMode,
  TicketPriority,
  TicketStatusBehavior,
} from '@21k/shared';
import { FormEvent, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  getApiErrorMessage,
  getLinkedProfile,
  getLinkedProfileChildren,
  patchTicketLinkedLearner,
  updateTicketCategory,
  updateTicketLabels,
  updateTicketPriority,
  updateTicketStatus,
} from '../../api/client';
import type {
  AssignableTeamGroup,
  LinkedProfile,
  LinkedProfileChild,
  TicketCategoryRef,
  TicketDetail,
  TicketLabelRef,
  TicketStatusRef,
  TicketStatusWorkflow,
} from '../../api/types';
import Badge from '../../components/Badge';
import ChannelIcon from '../../components/ChannelIcon';
import ConfirmDialog from '../../components/ConfirmDialog';
import LinkedProfileFullPanel from '../../components/LinkedProfileFullPanel';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { changeTicketAssigneeWithWarning } from '../../utils/ticketAssignee';
import { communicationModeBadgeClass } from './OverviewTab';
import { formatStatus, priorityBadgeClass, resolveAllowedStatusIds } from '../../utils/ticket';
import { ticketCreatorName, userInitials } from '../../utils/ticketDisplay';

const PROFILE_SOURCES = new Set(['ERP', 'PARENTHUB', 'FLOWW', 'GARAGES']);

type Props = {
  ticket: TicketDetail;
  canEdit: boolean;
  workflow: TicketStatusWorkflow | null;
  labelOptions: TicketLabelRef[];
  categoryOptions: TicketCategoryRef[];
  assignableTeams: AssignableTeamGroup[];
  onUpdated: () => void;
  onError: (message: string) => void;
  onReopen: () => void;
  reopening: boolean;
};

function formatCreated(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sourceBadgeClass(source: string): string {
  switch (source) {
    case 'FLOWW':
      return 'profile-source-badge floww';
    case 'GARAGES':
      return 'profile-source-badge garages';
    case 'ERP':
    case 'PARENTHUB':
      return 'profile-source-badge erp';
    default:
      return 'profile-source-badge';
  }
}

function sourceBadgeLabel(source: string): string {
  switch (source) {
    case 'PARENTHUB':
    case 'ERP':
      return '21K ERP';
    case 'GARAGES':
      return '21K Garages';
    case 'FLOWW':
      return '21K FLOWW';
    default:
      return source;
  }
}

type InlineSelectProps<T extends string> = {
  value: T;
  options: { value: T; label: string }[];
  onSave: (value: T) => Promise<void>;
  disabled?: boolean;
  renderValue: (value: T) => ReactNode;
};

function InlineSelectField<T extends string>({
  value,
  options,
  onSave,
  disabled,
  renderValue,
}: InlineSelectProps<T>) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (editing) selectRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  async function commit(next: T) {
    if (next === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      setDraft(value);
    } finally {
      setSaving(false);
    }
  }

  if (disabled) {
    return <span className="detail-row-value">{renderValue(value)}</span>;
  }

  if (editing) {
    return (
      <select
        ref={selectRef}
        className="form-control form-control-compact"
        value={draft}
        disabled={saving}
        onChange={(e) => {
          const next = e.target.value as T;
          setDraft(next);
          void commit(next);
        }}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDraft(value);
            setEditing(false);
          }
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <span className="detail-row-value">
      {renderValue(value)}
      <button
        type="button"
        className="edit-trigger"
        aria-label="Edit"
        onClick={() => setEditing(true)}
      >
        ✎
      </button>
    </span>
  );
}

export default function TicketDetailSidebar({
  ticket,
  canEdit,
  workflow,
  labelOptions,
  categoryOptions,
  assignableTeams,
  onUpdated,
  onError,
  onReopen,
  reopening,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [sidebarLabelsExpanded, setSidebarLabelsExpanded] = useState(false);
  const [profile, setProfile] = useState<LinkedProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [fullProfileOpen, setFullProfileOpen] = useState(false);
  const [linkChildren, setLinkChildren] = useState<LinkedProfileChild[]>([]);
  const [linkChildOpen, setLinkChildOpen] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const {
    confirm,
    confirmOpen,
    confirmMessage,
    handleConfirm,
    handleCancel,
  } = useConfirmDialog();

  const owner = ticket.currentOwner;
  const assignee = owner?.assignedTo ?? ticket.assignedTo;

  const allowedStatuses = useMemo(() => {
    if (!workflow) return [];
    const ids = resolveAllowedStatusIds(ticket.statusId, workflow);
    return workflow.statuses.filter((s) => ids.includes(s.id));
  }, [ticket.statusId, workflow]);

  const assigneeOptions = useMemo(() => {
    const seen = new Set<string>();
    const rows: { id: string; label: string }[] = [];
    for (const team of assignableTeams) {
      for (const member of team.members) {
        if (seen.has(member.userId)) continue;
        seen.add(member.userId);
        rows.push({
          id: member.userId,
          label: `${member.firstName} ${member.lastName} (${team.teamName})`,
        });
      }
    }
    return rows;
  }, [assignableTeams]);

  const priorityOptions = useMemo(
    () => Object.values(TicketPriority).map((p) => ({ value: p, label: p })),
    [],
  );

  const categorySelectOptions = useMemo(
    () => categoryOptions.map((c) => ({ value: c.id, label: c.name })),
    [categoryOptions],
  );

  const showLinkedProfile =
    ticket.linkedSource && PROFILE_SOURCES.has(ticket.linkedSource);

  useEffect(() => {
    if (!showLinkedProfile) return;
    let cancelled = false;
    setProfileLoading(true);
    void getLinkedProfile(ticket.id)
      .then((data) => {
        if (!cancelled) setProfile(data);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticket.id, showLinkedProfile]);

  useEffect(() => {
    if (!showLinkedProfile || !canEdit || ticket.linkedLearnerId) return;
    let cancelled = false;
    void getLinkedProfileChildren(ticket.id)
      .then((data) => {
        if (!cancelled) setLinkChildren(data.children);
      })
      .catch(() => {
        if (!cancelled) setLinkChildren([]);
      });
    return () => {
      cancelled = true;
    };
  }, [ticket.id, ticket.linkedLearnerId, showLinkedProfile, canEdit]);

  async function handleLinkChild(learnerId: string, enrollmentId: string) {
    setLinkSaving(true);
    onError('');
    try {
      await patchTicketLinkedLearner(ticket.id, { learnerId, enrollmentId });
      setLinkChildOpen(false);
      onUpdated();
    } catch (err) {
      onError(getApiErrorMessage(err));
    } finally {
      setLinkSaving(false);
    }
  }

  const ticketAboutName =
    ticket.linkedLearnerName ?? profile?.learnerName ?? null;

  async function handleAssigneeChange(assignedToId: string | null) {
    setSaving(true);
    onError('');
    try {
      const result = await changeTicketAssigneeWithWarning({
        ticketId: ticket.id,
        assignedToId,
        previousAssignedToId: ticket.assignedToId,
        assignableTeams,
        confirm,
      });
      if (result.applied) {
        onUpdated();
      }
    } catch (err) {
      onError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function runAction(fn: () => Promise<unknown>) {
    setSaving(true);
    onError('');
    try {
      await fn();
      onUpdated();
    } catch (err) {
      onError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusSubmit(e: FormEvent) {
    e.preventDefault();
    if (!newStatus || !statusComment.trim()) return;
    await runAction(() => updateTicketStatus(ticket.id, newStatus, statusComment.trim()));
    setNewStatus('');
    setStatusComment('');
  }

  async function handleRemoveLabel(labelId: string) {
    const next = ticket.labels.filter((l) => l.id !== labelId).map((l) => l.id);
    await runAction(() => updateTicketLabels(ticket.id, next));
  }

  async function handleToggleLabel(labelId: string) {
    const set = new Set(ticket.labels.map((l) => l.id));
    if (set.has(labelId)) set.delete(labelId);
    else set.add(labelId);
    await runAction(() => updateTicketLabels(ticket.id, [...set]));
  }

  const teamMeta = owner?.team?.name;
  const deptMeta = owner?.department.name ?? ticket.department.name;
  const assigneeMeta = [teamMeta, deptMeta].filter(Boolean).join(' · ');

  const sidebarLabelLimit = 3;
  const visibleSidebarLabels = sidebarLabelsExpanded
    ? ticket.labels
    : ticket.labels.slice(0, sidebarLabelLimit);
  const hiddenSidebarLabelCount = Math.max(0, ticket.labels.length - sidebarLabelLimit);

  return (
    <div className="ticket-detail-sidebar-inner">
      {/* ① STATUS */}
      {canEdit &&
        ((workflow && !ticket.status.isTerminal && allowedStatuses.length > 0) ||
          ticket.status.code === 'CANNOT_RESOLVE' ||
          ticket.status.onEnterBehavior === TicketStatusBehavior.CANNOT_RESOLVE) && (
        <section className="ticket-sidebar-section">
          <div className="sidebar-label">Status</div>
          {(ticket.status.code === 'CANNOT_RESOLVE' ||
            ticket.status.onEnterBehavior === TicketStatusBehavior.CANNOT_RESOLVE) && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onReopen}
              disabled={reopening}
            >
              {reopening ? 'Reopening…' : 'Reopen'}
            </button>
          )}
          {workflow && !ticket.status.isTerminal && allowedStatuses.length > 0 && (
          <form className="ticket-status-inline" onSubmit={(e) => void handleStatusSubmit(e)}>
            <select
              className="form-control form-control-compact"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={saving}
            >
              <option value="">{ticket.status.name}</option>
              {allowedStatuses.map((s: TicketStatusRef) => (
                <option key={s.id} value={s.id}>
                  {formatStatus(s)}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="btn btn-primary btn-compact"
              disabled={saving || !newStatus || !statusComment.trim()}
            >
              Update
            </button>
            {newStatus && (
              <textarea
                className="form-control ticket-status-comment"
                rows={2}
                placeholder="Comment required for status change"
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                disabled={saving}
              />
            )}
          </form>
          )}
        </section>
      )}

      {/* ② DETAILS */}
      <section className="ticket-sidebar-section">
        <div className="sidebar-label">Details</div>
        <div className="detail-row">
          <span className="detail-row-key">Priority</span>
          <InlineSelectField
            value={ticket.priority}
            options={priorityOptions}
            disabled={!canEdit || saving}
            renderValue={(p) => <Badge label={p} className={priorityBadgeClass(p)} />}
            onSave={async (p) => {
              await runAction(() => updateTicketPriority(ticket.id, p));
            }}
          />
        </div>
        <div className="detail-row">
          <span className="detail-row-key">Category</span>
          <InlineSelectField
            value={ticket.categoryId}
            options={categorySelectOptions}
            disabled={!canEdit || saving || categorySelectOptions.length === 0}
            renderValue={() => ticket.category.name}
            onSave={async (categoryId) => {
              await runAction(() => updateTicketCategory(ticket.id, categoryId));
            }}
          />
        </div>
        <div className="detail-row">
          <span className="detail-row-key">Channel</span>
          <span className="detail-row-value">
            <ChannelIcon code={ticket.channelType.code} />
            {ticket.channelType.name}
          </span>
        </div>
        <div className="detail-row">
          <span className="detail-row-key">Communication</span>
          <span className="detail-row-value">
            <Badge
              label={
                COMMUNICATION_MODE_LABELS[ticket.communicationMode as CommunicationMode] ??
                ticket.communicationMode
              }
              className={communicationModeBadgeClass(
                ticket.communicationMode as CommunicationMode,
              )}
            />
          </span>
        </div>
        <div className="detail-row">
          <span className="detail-row-key">Created by</span>
          <span className="detail-row-value">{ticketCreatorName(ticket)}</span>
        </div>
        <div className="detail-row">
          <span className="detail-row-key">Created</span>
          <span className="detail-row-value">{formatCreated(ticket.createdAt)}</span>
        </div>
        <div className="detail-row">
          <span className="detail-row-key">SLA deadline</span>
          <span className="detail-row-value">
            {ticket.slaDeadline ? formatCreated(ticket.slaDeadline) : '—'}
          </span>
        </div>
      </section>

      {/* ③ ASSIGNEE */}
      <section className="ticket-sidebar-section">
        <div className="sidebar-label">Assignee</div>
        {assignee ? (
          <>
            <div className="assignee-card">
              <span className="assignee-avatar">
                {userInitials(assignee.firstName, assignee.lastName)}
              </span>
              <div className="assignee-info">
                <div className="assignee-name">
                  {assignee.firstName} {assignee.lastName}
                </div>
                {assigneeMeta && <div className="assignee-meta">{assigneeMeta}</div>}
              </div>
            </div>
            {canEdit && (
              <>
                <button
                  type="button"
                  className="link-btn ticket-reassign-link"
                  onClick={() => setReassignOpen((v) => !v)}
                >
                  Reassign →
                </button>
                {reassignOpen && (
                  <select
                    className="form-control form-control-compact ticket-reassign-select"
                    value={ticket.assignedToId ?? ''}
                    disabled={saving}
                    onChange={(e) => {
                      void handleAssigneeChange(e.target.value || null);
                      setReassignOpen(false);
                    }}
                  >
                    <option value="">Unassigned</option>
                    {assigneeOptions.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}
          </>
        ) : (
          <p className="text-muted">Unassigned</p>
        )}
      </section>

      {/* ④ LABELS */}
      <section className="ticket-sidebar-section">
        <div className="sidebar-label">Labels</div>
        <div className="ticket-label-tags">
          {visibleSidebarLabels.map((label) => (
            <span key={label.id} className="label-tag">
              {label.name}
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Remove ${label.name}`}
                  onClick={() => void handleRemoveLabel(label.id)}
                  disabled={saving}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {ticket.labels.length === 0 && (
            <span className="text-muted ticket-labels-empty">No labels</span>
          )}
        </div>
        {!sidebarLabelsExpanded && hiddenSidebarLabelCount > 0 && (
          <button
            type="button"
            className="ticket-labels-toggle"
            onClick={() => setSidebarLabelsExpanded(true)}
          >
            + {hiddenSidebarLabelCount} more
          </button>
        )}
        {sidebarLabelsExpanded && ticket.labels.length > sidebarLabelLimit && (
          <button
            type="button"
            className="ticket-labels-toggle"
            onClick={() => setSidebarLabelsExpanded(false)}
          >
            ↑ show less
          </button>
        )}
        {canEdit && labelOptions.length > 0 && (
          <>
            <button
              type="button"
              className="link-btn ticket-add-label-btn"
              onClick={() => setLabelsOpen((v) => !v)}
            >
              + Add label
            </button>
            {labelsOpen && (
              <div className="ticket-label-picker-popover">
                {labelOptions.map((l) => {
                  const checked = ticket.labels.some((x) => x.id === l.id);
                  return (
                    <label key={l.id} className="ticket-label-picker-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={saving}
                        onChange={() => void handleToggleLabel(l.id)}
                      />
                      <span>{l.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>

      {/* ⑤ CUSTOMER PROFILE */}
      {showLinkedProfile && (
        <section className="ticket-sidebar-section">
          <div className="sidebar-label">Customer Profile</div>
          {profileLoading && <p className="text-muted">Loading profile…</p>}
          {!profileLoading && !profile && ticket.linkedLearnerId && ticketAboutName && (
            <div className="ticket-about-chip">
              <User size={14} strokeWidth={2} aria-hidden />
              <span>
                Ticket is about: <strong>{ticketAboutName}</strong>
              </span>
            </div>
          )}
          {!profileLoading && profile && (
            <div className="ticket-linked-profile">
              {ticket.linkedLearnerId && ticketAboutName ? (
                <div className="ticket-about-chip">
                  <User size={14} strokeWidth={2} aria-hidden />
                  <span>
                    Ticket is about: <strong>{ticketAboutName}</strong>
                  </span>
                </div>
              ) : (
                <p className="ticket-about-muted">Not linked to a specific child</p>
              )}

              {!ticket.linkedLearnerId && (
                <div className="ticket-child-missing-warning">
                  Child not specified — was this ticket created before the child selector was
                  added? Ask the agent to link manually.
                </div>
              )}

              {canEdit && !ticket.linkedLearnerId && linkChildren.length > 0 && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary btn-compact ticket-link-child-btn"
                    disabled={linkSaving}
                    onClick={() => setLinkChildOpen((v) => !v)}
                  >
                    Link child
                  </button>
                  {linkChildOpen && (
                    <select
                      className="form-control form-control-compact ticket-link-child-select"
                      defaultValue=""
                      disabled={linkSaving}
                      onChange={(e) => {
                        const child = linkChildren.find((c) => c.learnerId === e.target.value);
                        if (child) void handleLinkChild(child.learnerId, child.enrollmentId);
                      }}
                    >
                      <option value="" disabled>
                        Select a child…
                      </option>
                      {linkChildren.map((child) => (
                        <option key={child.learnerId} value={child.learnerId}>
                          {child.learnerName} · {child.grade}
                          {child.enrollmentId ? ` · ${child.enrollmentId}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              )}

              <span className={sourceBadgeClass(profile.source)}>
                {sourceBadgeLabel(profile.source)}
              </span>
              <div className="ticket-linked-profile-names">
                <div className="ticket-linked-learner">{profile.learnerName}</div>
                {profile.parentName && (
                  <div className="ticket-linked-parent">{profile.parentName}</div>
                )}
              </div>
              {(profile.email || profile.phone) && (
                <div className="ticket-linked-contact">
                  {profile.email && (
                    <span className="ticket-linked-contact-line">
                      <Mail size={12} strokeWidth={2} aria-hidden />
                      <span title={profile.email}>{profile.email}</span>
                    </span>
                  )}
                  {profile.phone && (
                    <span className="ticket-linked-contact-line">
                      <Phone size={12} strokeWidth={2} aria-hidden />
                      <span>{profile.phone}</span>
                    </span>
                  )}
                </div>
              )}
              <div className="ticket-linked-grid">
                <div>
                  <span className="detail-row-key">Enrollment ID</span>
                  <span>{profile.enrollmentId || '—'}</span>
                </div>
                <div>
                  <span className="detail-row-key">Grade</span>
                  <span>{profile.grade || '—'}</span>
                </div>
                <div>
                  <span className="detail-row-key">Entity</span>
                  <span>{profile.entity || '—'}</span>
                </div>
                <div>
                  <span className="detail-row-key">Pathway</span>
                  <span>{profile.curriculum || '—'}</span>
                </div>
              </div>
              <button
                type="button"
                className="link-btn ticket-profile-link"
                onClick={() => setFullProfileOpen(true)}
              >
                View full profile →
              </button>
            </div>
          )}
          {!profileLoading && !profile && (
            <p className="text-muted">Profile unavailable</p>
          )}
        </section>
      )}

      {fullProfileOpen && (
        <LinkedProfileFullPanel
          ticketId={ticket.id}
          currentTicketId={ticket.id}
          linkedLearnerId={ticket.linkedLearnerId}
          linkedLearnerName={ticket.linkedLearnerName}
          linkedEnrollmentId={ticket.linkedEnrollmentId}
          onClose={() => setFullProfileOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm assignment"
        message={confirmMessage}
        confirmLabel="Proceed"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}